import { useMemo, useState } from "react";

export default function TransactionSummary({
  formData,
  setFormData,
  onConfirm,
  loading: parentLoading,
  onPrev,
}) {
  const [error, setError] = useState("");

  const safeForm = formData || {};

  const {
    countryFrom,
    countryTo,
    method,
    calculation = {},
    sender = {},
    beneficiary = {},
    currency,
    recipientCurrency,
  } = safeForm;

  const {
    amountToSend,
    convertedAmount,
    fee,
    totalToPay,
    currencyFrom,
    ruleCode,
    rateSource,
  } = calculation || {};

  const loading = !!parentLoading;

  const senderFullName =
    sender.fullName ||
    `${sender.firstName || ""} ${sender.lastName || ""}`.trim() ||
    "—";

  const beneficiaryFullName =
    beneficiary.fullName ||
    `${beneficiary.firstName || ""} ${beneficiary.lastName || ""}`.trim() ||
    "—";

  const isValidBeneficiary = useMemo(() => {
    if (!method) return false;
    if (method === "MOBILE_MONEY")
      return !!(beneficiary.phone && beneficiary.operator);
    if (method === "BANK_TRANSFER")
      return !!(beneficiary.bankName && beneficiary.accountNumber);
    if (method === "INTERAC") return !!beneficiary.interacEmail;
    return true;
  }, [method, beneficiary]);

  const canSubmit = useMemo(() => {
    const a = Number(amountToSend);
    const ca = Number(convertedAmount);
    const f = Number(fee);
    const t = Number(totalToPay);

    return (
      Number.isFinite(a) &&
      a > 0 &&
      Number.isFinite(ca) &&
      ca > 0 &&
      Number.isFinite(f) &&
      f >= 0 &&
      Number.isFinite(t) &&
      t > 0 &&
      isValidBeneficiary
    );
  }, [amountToSend, convertedAmount, fee, totalToPay, isValidBeneficiary]);

  const fmt = (n) => {
    const x = Number(n);
    return Number.isFinite(x) ? x.toFixed(2) : "0.00";
  };

  const handleSubmit = async () => {
    try {
      setError("");
      await onConfirm?.();
    } catch (e) {
      setError(e?.message || "Transaction échouée");
    }
  };

  if (!formData) return null;

  const curFrom = currencyFrom || currency || "";
  const curTo = recipientCurrency || "";

  return (
    <div className="space-y-6 font-roboto">
      <div>
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white">
          Résumé de la transaction
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Vérifiez les informations avant de confirmer.
        </p>
      </div>

      <Card title="Montants">
        <Row label="Envoyer depuis" value={countryFrom || "—"} />
        <Row label="Envoyer vers" value={countryTo || "—"} />
        <Row label="Méthode" value={methodLabel(method)} />

        <Divider />

        <Row
          label="Montant envoyé"
          value={`${fmt(amountToSend)} ${curFrom}`}
          mono
        />

        <Row
          label="Frais"
          value={
            <span className="text-emerald-600 font-bold">
              {fmt(fee)} {curFrom}
            </span>
          }
        />

        <Row
          label="Total à payer"
          value={
            <span className="text-emerald-600 font-extrabold">
              {fmt(totalToPay)} {curFrom}
            </span>
          }
          highlight
        />

        <Row
          label="Montant reçu"
          value={
            <span className="text-emerald-700 dark:text-emerald-300 font-extrabold">
              {fmt(convertedAmount)} {curTo}
            </span>
          }
          highlight
        />

        <Row label="Règle appliquée" value={ruleCode || "—"} wrap />
        <Row label="Source du taux" value={rateSource || "fixed"} wrap />
      </Card>

      <Card title="Mode de paiement">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Choisissez comment régler le montant total de votre transaction.
        </p>

        <label className="flex items-start gap-3 rounded-xl border border-black/10 dark:border-white/10 p-4 cursor-pointer">
          <input
            type="radio"
            name="paymentProvider"
            value="MANUAL"
            checked={(safeForm.paymentProvider || "MANUAL") === "MANUAL"}
            onChange={() =>
              setFormData((prev) => ({ ...prev, paymentProvider: "MANUAL" }))
            }
            className="mt-1"
          />
          <span>
            <span className="block font-semibold text-slate-900 dark:text-white">
              Paiement manuel
            </span>
            <span className="block text-sm text-slate-500 dark:text-slate-400">
              Recevoir les instructions de paiement habituelles.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-xl border border-indigo-500/40 bg-indigo-50/50 dark:bg-indigo-500/10 p-4 cursor-pointer">
          <input
            type="radio"
            name="paymentProvider"
            value="STRIPE"
            checked={safeForm.paymentProvider === "STRIPE"}
            onChange={() =>
              setFormData((prev) => ({ ...prev, paymentProvider: "STRIPE" }))
            }
            className="mt-1"
          />
          <span>
            <span className="block font-semibold text-slate-900 dark:text-white">
              Payer par carte avec Stripe
            </span>
            <span className="block text-sm text-slate-500 dark:text-slate-400">
              Saisir les informations de paiement dans un formulaire sécurisé
              intégré.
            </span>
          </span>
        </label>

        {safeForm.paymentProvider === "STRIPE" && (
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800 p-3 text-sm">
            <span className="font-semibold">Montant à régler : </span>
            {fmt(totalToPay)} {curFrom}
          </div>
        )}
      </Card>

      <Card title="Expéditeur">
        <Row label="Nom" value={senderFullName} />
        <Row label="Email" value={sender.email || "—"} wrap />
        <Row label="Téléphone" value={sender.phone || "—"} wrap />
      </Card>

      <Card title="Bénéficiaire">
        <Row label="Nom" value={beneficiaryFullName} />
        {beneficiary.email ? (
          <Row label="Email" value={beneficiary.email || "—"} wrap />
        ) : null}

        {method === "MOBILE_MONEY" && (
          <>
            <Divider />
            <Row label="Opérateur" value={beneficiary.operator || "—"} />
            <Row label="Téléphone" value={beneficiary.phone || "—"} wrap />
          </>
        )}

        {method === "BANK_TRANSFER" && (
          <>
            <Divider />
            <Row label="Banque" value={beneficiary.bankName || "—"} wrap />
            <Row
              label="Compte"
              value={beneficiary.accountNumber || "—"}
              wrap
              mono
            />
          </>
        )}

        {method === "INTERAC" && (
          <>
            <Divider />
            <Row
              label="Email Interac"
              value={beneficiary.interacEmail || "—"}
              wrap
            />
          </>
        )}
      </Card>

      {error && (
        <div className="rounded-2xl border border-red-500/20 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onPrev}
          className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 py-3 font-medium text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
        >
          Retour
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          className="rounded-2xl bg-slate-900 text-white py-3 font-semibold hover:opacity-95 transition
                     disabled:opacity-50 disabled:cursor-not-allowed
                     dark:bg-white dark:text-slate-900"
        >
          {loading ? "Traitement…" : "Confirmer la transaction"}
        </button>
      </div>

      {!canSubmit && (
        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
          Veuillez compléter les informations requises.
        </p>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-4 sm:p-5 space-y-3">
      <div className="text-sm font-semibold text-slate-900 dark:text-white">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-black/5 dark:bg-white/10 my-2" />;
}

function Row({ label, value, highlight, wrap, mono }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3">
      <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <span
        className={[
          "text-sm text-slate-900 dark:text-white",
          highlight ? "font-semibold" : "",
          wrap
            ? "break-words text-right sm:text-left"
            : "text-right sm:text-left",
          mono ? "tabular-nums" : "",
          "sm:max-w-[65%]",
        ].join(" ")}
      >
        {value}
      </span>
    </div>
  );
}

function methodLabel(method) {
  switch (method) {
    case "INTERAC":
      return "Interac e-Transfer";
    case "MOBILE_MONEY":
      return "Mobile Money";
    case "BANK_TRANSFER":
      return "Virement bancaire";
    default:
      return method || "—";
  }
}
